import { rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { chakra } from '@chakra-ui/react/styled-system';
import { memo, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { type Range } from './LogProcessor';

type LogChunkProps = {
  index: number;
  wrap?: boolean;
  content: string;
  range: Range;
  colorize?: (index: number) => string | undefined;
};

const LINE_HEIGHT = 21;
const MODE_SWITCH_THRESHOLD = 50;

const LogChunk = memo(({ index, wrap, content, range, colorize }: LogChunkProps) => {
  const [isColorized, setIsColorized] = useState(false);
  const [colorizedContent, setColorizedContent] = useState<string>();
  const { isIntersecting, ref } = useIntersectionObserver();
  const hoverBoxRef = useRef<HTMLDivElement | null>(null);
  const hoverLineRef = useRef<number | null>(null);

  // Colorizing is ~500x the raw pass (mostly linkify), so defer it until the chunk is on screen.
  // No dedupe guard: colorize is pure, so a rare double call is harmless.
  useEffect(() => {
    if (isIntersecting && !colorizedContent) {
      const result = colorize?.(index);
      if (result) {
        setColorizedContent(result);
      }
    }
  }, [isIntersecting, colorizedContent, index, colorize]);

  useEffect(() => {
    if (isIntersecting && !isColorized && colorizedContent) {
      const timeoutId = window.setTimeout(() => {
        if (isIntersecting) {
          setIsColorized(true);
        }
      }, MODE_SWITCH_THRESHOLD);
      return () => window.clearTimeout(timeoutId);
    }

    if (!isIntersecting && isColorized) {
      const timeoutId = window.setTimeout(() => {
        if (!isIntersecting) {
          setIsColorized(false);
        }
      }, MODE_SWITCH_THRESHOLD);
      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [isIntersecting, isColorized, colorizedContent]);

  const handleMouseMove: MouseEventHandler<HTMLElement> = ({ currentTarget, clientY }) => {
    if (!isColorized || !hoverBoxRef.current) {
      return;
    }

    const rect = currentTarget.getBoundingClientRect();
    const line = Math.floor((clientY - rect.top) / LINE_HEIGHT);

    if (hoverLineRef.current === line) {
      return;
    }

    hoverLineRef.current = line;
    hoverBoxRef.current.style.transform = `translateY(${line * LINE_HEIGHT}px)`;
    hoverBoxRef.current.style.display = 'block';
  };

  const handleMouseLeave: MouseEventHandler<HTMLElement> = () => {
    if (!isColorized || !hoverBoxRef.current) {
      return;
    }

    hoverLineRef.current = null;
    hoverBoxRef.current.style.display = 'none';
  };

  return (
    <Box
      minWidth="100%"
      position="relative"
      className="log-chunk"
      width={wrap ? 'auto' : 'max-content'}
      style={{ contain: 'layout' }}
      id={`log-chunk-${index}-${range[0]}-${range[1]}`}
    >
      <chakra.pre
        ref={ref}
        fontSize={rem(14)}
        fontFamily="mono"
        data-index={index}
        className="log-chunk-content"
        lineHeight={`${LINE_HEIGHT}px`}
        whiteSpace={wrap ? 'pre-wrap' : 'pre'}
        onMouseMove={isColorized ? handleMouseMove : undefined}
        onMouseLeave={isColorized ? handleMouseLeave : undefined}
        dangerouslySetInnerHTML={{
          __html: isColorized && colorizedContent ? colorizedContent : content,
        }}
        css={{
          WebkitFontSmoothing: 'subpixel-antialiased',
          lineHeight: `${LINE_HEIGHT}px !important`,
          height: 'auto',
          a: {
            ':hover': {
              textDecoration: 'underline',
            },
          },
          '& span': {
            lineHeight: `${LINE_HEIGHT}px !important`,
          },
        }}
      />
      <Box
        left={0}
        right={0}
        top={0}
        ref={hoverBoxRef}
        height={`${LINE_HEIGHT}px`}
        position="absolute"
        pointerEvents="none"
        className="log-chunk-line--hovered"
        display="none"
        backgroundColor="var(--colors-neutral-30)"
        mixBlendMode="lighten"
      />
    </Box>
  );
});
LogChunk.displayName = 'LogChunk';

export default LogChunk;
